import { Injector } from '@angular/core';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { Observable } from 'rxjs/Observable';
import { UserModel } from '../../authentication/models/user.model';
import { Observer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { combineLatest } from 'rxjs/observable/combineLatest';

interface Options {

    order?: number;

    permission?: Array<string> | string;

    role?: Array<string> | string;

}

export class MenuModel {

    protected items: Array<MenuModel> = [];

    /**
     * @param name
     * @param label
     * @param routeName
     * @param options
     * @param injector
     */
    public constructor(
        public name: string,
        public label: string,
        public routeName: string | Array<string | number>,
        public options: Options = {},
        protected injector: Injector,
    ) {
        this.options.order = this.options.order
            || 100;
    }

    /**
     * Return all items.
     *
     * @returns {Array}
     */
    public children() {

        return this.items;

    }

    /**
     * Check if item has children.
     *
     * @returns {boolean}
     */
    public hasChildren() {

        return this.items.length > 0;

    }

    /**
     * Add a new item to the menu.
     *
     * @param name
     * @param label
     * @param route
     * @param options
     * @returns {MenuModel}
     */
    public add(name: string, label: string, route: string | Array<string>, options: Object = {}) {

        if (this.exists(name)) {
            throw new Error('Menu\'s with the same name (' + name + ') are not allowed');
        }

        options['order'] = options['order'] ? options['order'] : 100;

        const menu = new MenuModel(name, label, route, options, this.injector);

        this.items = this.items.concat([menu]);

        this.reorder();

        return this;

    }

    /**
     * Update an existing menu item.
     *
     * @param {string} name
     * @param {{[p: string]: any}} attributes
     * @returns {MenuModel}
     */
    public update(name: string, attributes: { [key: string]: any }): MenuModel {
        const menu = this.get(name);

        if (menu) {
            // Loop trough the list of given attributes.
            for (const attribute in attributes) {
                if (attributes.hasOwnProperty(attribute)) {

                    // Check if the attribute is an object which can contain other attributes.
                    if (attributes[attribute] instanceof Object) {
                        const object          = attributes[attribute];
                        const parentAttribute = attribute;

                        // If the attribute is an object, we'll loop trough it and update the given
                        // attributes on the object.
                        for (const attribute in object) {
                            if (object.hasOwnProperty(attribute)) {

                                // Update the attribute.
                                menu[parentAttribute][attribute] = object[attribute];
                            }
                        }
                    } else {
                        // Update the attribute.
                        menu[attribute] = attributes[attribute];
                    }
                }
            }

            this.reorder();
        } else {
            console.warn('Menu with the name (' + name + ') could not be found.');
        }

        return menu;
    }

    /**
     * Get a menu item by the given name.
     *
     * @param name
     */
    public get(name: string): MenuModel {
        return this.items.find((i) => i.name === name);
    }

    /**
     * Delete item by name from the menu.
     *
     * @param name
     */
    public delete(name: string): this {
        this.items = this.items.filter((i) => i.name !== name);
        this.reorder();
        return this;
    }

    /**
     * See if menu exists already.
     *
     * @param id
     */
    public exists(id: any): boolean {
        return !!this.get(id);
    }

    /**
     * Indicates if the menu item can be shown.
     */
    public canShow(): Observable<boolean> {
        return new Observable((observer) => {
            if (!this.options.permission && !this.options.role) {
                observer.next(true);
                return;
            }

            const permissions = this.options.permission instanceof Array
                ? this.options.permission
                : [this.options.permission];

            const roles = this.options.role instanceof Array
                ? this.options.role
                : [this.options.role];

            const combined = combineLatest(
                this.userHasPermissions(permissions),
                this.userHasRoles(roles),
            );

            combined
                .subscribe(([hasPermissions, hasRoles]) => {
                    observer.next(hasPermissions || hasRoles);
                }, (error) => {
                    observer.error(error);
                }, () => {
                    observer.complete();
                });
        });
    }

    /**
     * Get the authenticated user.
     */
    protected getAuthenticatedUser(): Observable<UserModel> {
        return new Observable((observer: Observer<UserModel>) => {
            const auth = this.injector.get<AuthenticationService>(AuthenticationService);
            return auth.user()
                .pipe(
                    filter((user: UserModel | null) => {
                        return !!user;
                    })
                )
                .subscribe((user: UserModel) => {
                    observer.next(user);
                }, (error) => {
                    observer.error(error);
                }, () => {
                    observer.complete();
                });
        });
    }

    /**
     * Indicates if the authenticated user has the given permissions.
     *
     * @param permissions The list of permissions.
     */
    protected userHasPermissions(permissions: Array<string>): Observable<boolean> {
        return new Observable((observer: Observer<boolean>) => {
            this.getAuthenticatedUser()
                .subscribe((user: UserModel) => {
                    permissions.forEach((permission) => {
                        const hasPermission = user.can(permission);

                        if (!hasPermission) {
                            observer.next(false);
                            observer.complete();
                            return;
                        }
                    });

                    observer.next(true);
                    observer.complete();
                }, (error) => {
                    observer.error(error);
                })
        });
    }

    /**
     * Indicates if the authenticated user has the given roles.
     *
     * @param roles The list of roles.
     */
    protected userHasRoles(roles: Array<string>): Observable<boolean> {
        return new Observable((observer: Observer<boolean>) => {
            this.getAuthenticatedUser()
                .subscribe((user: UserModel) => {
                    observer.next(
                        user.hasRoleIn(roles)
                    );
                    observer.complete();
                }, (error) => {
                    observer.error(error);
                })
        });
    }

    /**
     * Reorder items
     */
    private reorder() {

        this.items.sort((a, b): any => {
            if (a.options.order !== undefined && b.options.order !== undefined) {
                return a.options.order - b.options.order;
            } else {
                return false;
            }
        });

    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/menu/models/menu.model.ts